import React from 'react';
import { Button, FadeIn } from './ui';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PlayIcon, PlayCircleIcon, PresentationIcon, HeadphonesIcon, MessageSquareIcon, XIcon, CheckIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-28 pb-24 md:pt-36 md:pb-32 overflow-hidden bg-white" data-id="element-4187">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-4188">
        <motion.div animate={{
        x: [0, 30, 0],
        y: [0, -20, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.07] rounded-full blur-3xl" data-id="element-4189" />
        <motion.div animate={{
        x: [0, -25, 0],
        y: [0, 30, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.06] rounded-full blur-3xl" data-id="element-4190" />
      </div>

      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(to right, rgb(15 23 42 / 0.04) 1px, transparent 1px), linear-gradient(to bottom, rgb(15 23 42 / 0.04) 1px, transparent 1px)',
      backgroundSize: '56px 56px',
      maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
      WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
    }} data-id="element-4191" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-4192">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center" data-id="element-4193">
          {/* Left Column: Copy */}
          <div className="lg:col-span-5 max-w-xl" data-id="element-4194">
            <FadeIn data-id="element-4195">
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-7 shadow-sm" data-id="element-4196">
                <span className="bg-brand-coral/10 text-brand-coral text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider" data-id="element-4197">
                  New
                </span>
                <span className="text-sm font-medium text-slate-600" data-id="element-4198">
                  AI Offsite Designer is live
                </span>
                <ArrowRightIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4199" />
              </div>
            </FadeIn>

            <FadeIn delay={0.05} data-id="element-4200">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6" data-id="element-4201">
                Turn any offsite into a{' '}
                <span className="relative inline-block whitespace-nowrap" data-id="element-4202">
                  <span className="relative z-10 text-brand-navy" data-id="element-4203">
                    structured,
                  </span>
                  <span className="absolute bottom-1 left-0 right-0 h-3 bg-brand-coral/20 -z-0" data-id="element-4204"></span>
                </span>{' '}
                <span className="text-brand-teal" data-id="element-4205">measurable</span> experience.
              </h1>
            </FadeIn>

            <FadeIn delay={0.06} data-id="element-4206">
              <h2 className="hidden text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6 text-brand-navy" data-id="element-4207">
                Retire the{' '}
                <span className="relative inline-block" data-id="element-4208">
                  <span className="relative z-10 line-through decoration-brand-coral decoration-[3px]" data-id="element-4209">
                    paper agenda
                  </span>
                </span>
                . Ship a{' '}
                <span className="text-brand-teal" data-id="element-4210">live experience</span>.
              </h2>
            </FadeIn>

            <FadeIn delay={0.15} data-id="element-4211">
              <p className="text-lg md:text-xl text-slate-600 mb-9 leading-relaxed text-balance max-w-xl" data-id="element-4212">
                The AI-powered experience layer for hotels, DMCs, MICE planners,
                and corporate teams. Retire the paper agenda — ship a live
                branded hub that turns ordinary meetings into events attendees
                rave about, with measurable outcomes leadership can point to.
              </p>
            </FadeIn>

            <FadeIn delay={0.25} data-id="element-4213">
              <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-4214">
                <Button variant="primary" size="lg" href="/designer" className="group" data-id="element-4215">
                  Design Your Free Offsite
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4216" />
                </Button>
              </div>
            </FadeIn>

            <FadeIn delay={0.35} data-id="element-4217">
              <div className="border-t border-slate-100 pt-7" data-id="element-4218">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4" data-id="element-4219">
                  Trusted by teams designing better events
                </p>
                <div className="flex flex-wrap items-center gap-x-7 gap-y-3 text-slate-400" data-id="element-4220">
                  {['MARRIOTT', 'HILTON', 'CVENT', 'IHG', 'ACCOR'].map(b => <span key={b} className="font-bold text-sm tracking-[0.2em] hover:text-slate-600 transition-colors cursor-default" data-id="element-4221">
                      {b}
                    </span>)}
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Right Column: Before / After comparison */}
          <div className="lg:col-span-7 relative" data-id="element-4222">
            <div className="relative h-[620px] md:h-[680px] flex items-center justify-center" data-id="element-4223">
              {/* THEN label */}
              <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="absolute top-2 left-2 md:left-4 z-30 flex items-center gap-2 bg-slate-900/85 backdrop-blur text-white text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full" data-id="element-4224">
                <XIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4225" /> Then · paper
              </motion.div>

              {/* NOW label */}
              <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} className="absolute -top-3 right-12 md:right-20 z-40 flex items-center gap-2 bg-brand-coral text-white text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full shadow-lg shadow-brand-coral/40" data-id="element-4226">
                <CheckIcon className="w-3.5 h-3.5" data-id="element-4227" /> Now · Offsite+
              </motion.div>

              {/* Paper agenda — left, tilted, faded */}
              <motion.div initial={{
              opacity: 0,
              x: -40,
              rotate: -10
            }} animate={{
              opacity: 1,
              x: 0,
              rotate: -6
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} className="absolute left-0 md:left-4 top-12 z-10" style={{
              transformOrigin: 'center center'
            }} data-id="element-4228">
                <PaperAgenda data-id="element-4229" />
              </motion.div>

              {/* Connector arrow */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.9,
              duration: 0.5
            }} className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white shadow-xl border border-slate-200 items-center justify-center" data-id="element-4230">
                <ArrowRightIcon className="w-5 h-5 text-brand-teal" data-id="element-4231" />
              </motion.div>

              {/* Phone — right, prominent */}
              <motion.div initial={{
              opacity: 0,
              x: 40,
              y: 20,
              rotate: 6
            }} animate={{
              opacity: 1,
              x: 0,
              y: 0,
              rotate: 3
            }} transition={{
              duration: 0.9,
              delay: 0.2,
              ease: 'easeOut'
            }} whileHover={{
              rotate: 0,
              y: -6
            }} className="absolute right-0 md:right-4 z-20" style={{
              transformOrigin: 'center center'
            }} data-id="element-4232">
                <PhoneHub data-id="element-4233" />
              </motion.div>

              {/* Floating chips removed for cleaner hero */}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
/* ─────── Paper agenda mockup ─────── */
function PaperAgenda() {
  return <div className="relative" data-id="element-4234">
      {/* Stacked papers behind for depth */}
      <div className="absolute -bottom-2 left-3 right-1 h-full bg-amber-50/80 rounded-sm shadow-md rotate-[2deg]" data-id="element-4235" />
      <div className="absolute -bottom-1 left-1.5 right-2 h-full bg-stone-50 rounded-sm shadow-md rotate-[1deg]" data-id="element-4236" />

      <div className="relative w-[260px] sm:w-[300px] bg-[#fdfcf6] rounded-sm shadow-2xl shadow-slate-400/40 border border-stone-200 p-6 font-serif" style={{
      backgroundImage: 'repeating-linear-gradient(transparent 0, transparent 23px, rgba(15,23,42,0.05) 23px, rgba(15,23,42,0.05) 24px)'
    }} data-id="element-4237">
        {/* Punch holes */}
        <div className="absolute left-2 top-8 w-2.5 h-2.5 rounded-full bg-stone-200 shadow-inner" data-id="element-4238" />
        <div className="absolute left-2 top-1/2 w-2.5 h-2.5 rounded-full bg-stone-200 shadow-inner" data-id="element-4239" />
        <div className="absolute left-2 bottom-8 w-2.5 h-2.5 rounded-full bg-stone-200 shadow-inner" data-id="element-4240" />

        <div className="pl-4" data-id="element-4241">
          <div className="text-[9px] uppercase tracking-[0.25em] text-stone-500 mb-2" data-id="element-4242">
            Conference Agenda
          </div>
          <div className="text-lg font-bold text-stone-800 leading-tight mb-1" data-id="element-4243">
            Annual Leadership Summit
          </div>
          <div className="text-[10px] text-stone-500 mb-5 italic" data-id="element-4244">
            May 12–14 · Please retain this booklet
          </div>

          <div className="space-y-2.5 text-stone-700" data-id="element-4245">
            {[['09:00', 'Registration & coffee'], ['10:00', 'Welcome remarks'], ['11:00', 'Keynote: Strategy 2026'], ['12:30', 'Lunch · Ballroom B'], ['14:00', 'Breakout sessions'], ['15:30', 'Panel discussion'], ['17:00', 'Reception · Atrium'], ['19:00', 'Dinner']].map(([t, l]) => <div key={l} className="flex gap-3 text-[11px]" data-id="element-4246">
                <span className="font-mono text-stone-500 w-10 shrink-0" data-id="element-4247">
                  {t}
                </span>
                <span data-id="element-4248">{l}</span>
              </div>)}
          </div>

          <div className="mt-5 pt-3 border-t border-dashed border-stone-300 text-[9px] text-stone-400 italic" data-id="element-4249">
            Wi-Fi: GuestNetwork · Pwd: welcome2026
          </div>
        </div>

        {/* Coffee stain */}
        <div className="absolute bottom-6 right-4 w-12 h-12 rounded-full bg-amber-900/10 blur-[1px]" data-id="element-4250" />
        <div className="absolute bottom-7 right-5 w-9 h-9 rounded-full border-2 border-amber-900/15" data-id="element-4251" />
      </div>
    </div>;
}
/* ─────── Phone Hub mockup ─────── */
function PhoneHub() {
  return <div className="relative w-[260px] sm:w-[300px]" data-id="element-4252">
      <div className="bg-slate-900 rounded-[2.5rem] p-2.5 shadow-2xl shadow-slate-900/40 border border-slate-800" data-id="element-4253">
        {/* Notch */}
        <div className="relative" data-id="element-4254">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-b-2xl z-20" data-id="element-4255" />

          <div className="relative bg-white rounded-[2rem] overflow-hidden h-[560px] flex flex-col" data-id="element-4256">
            {/* Status bar — fixed top, sits below notch */}
            <div className="shrink-0 px-6 pt-7 pb-2 flex justify-between items-center text-[10px] font-bold text-slate-700 bg-white relative z-10" data-id="element-4257">
              <span data-id="element-4258">9:41</span>
              <span data-id="element-4259">•••</span>
            </div>

            {/* App content — scrollable */}
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3 scrollbar-thin" data-id="element-4260">
              {/* Welcome */}
              <div className="bg-gradient-to-br from-brand-navy to-brand-teal text-white p-4 rounded-2xl" data-id="element-4261">
                <div className="text-[11px] font-medium opacity-80 mb-0.5" data-id="element-4262">
                  Welcome, Sarah 👋
                </div>
                <div className="text-base font-bold leading-tight" data-id="element-4263">
                  Day 1 of Leadership Reset
                </div>
              </div>

              {/* Live agenda */}
              <div className="bg-white border border-slate-200 rounded-xl p-3" data-id="element-4264">
                <div className="flex items-center justify-between mb-2" data-id="element-4265">
                  <h3 className="font-bold text-[11px] text-brand-navy" data-id="element-4266">
                    Today
                  </h3>
                  <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-4267">
                    <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" data-id="element-4268" />
                    Live
                  </span>
                </div>
                <div className="space-y-1.5" data-id="element-4269">
                  {[['9:00', 'Welcome circle', true], ['10:30', 'Team Trust workshop', false], ['14:00', 'Wellness break', false]].map(([t, l, active]) => <div key={l as string} className="flex items-center gap-2 text-[10px]" data-id="element-4270">
                      <span className="font-mono text-slate-500 w-10" data-id="element-4271">
                        {t as string}
                      </span>
                      <span className={`flex-1 truncate ${active ? 'text-brand-navy font-bold' : 'text-slate-600'}`} data-id="element-4272">
                        {l as string}
                      </span>
                      {active && <span className="w-1.5 h-1.5 rounded-full bg-brand-coral" data-id="element-4273" />}
                    </div>)}
                </div>
              </div>

              {/* Slides tile */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-4274">
                <div className="relative aspect-[4/2.6] bg-gradient-to-br from-brand-navy via-slate-800 to-brand-teal text-white p-2.5 flex flex-col justify-between" data-id="element-4275">
                  <div className="flex items-center justify-between text-[8px] font-bold uppercase tracking-wider" data-id="element-4276">
                    <span className="flex items-center gap-1 text-teal-300" data-id="element-4277">
                      <PresentationIcon className="w-2.5 h-2.5" data-id="element-4278" /> Slides · 4/18
                    </span>
                  </div>
                  <div className="text-[10px] font-bold leading-snug" data-id="element-4279">
                    Trust is the multiplier of every decision.
                  </div>
                  <div className="absolute bottom-1.5 left-2 right-2 flex gap-0.5" data-id="element-4280">
                    {Array.from({
                    length: 6
                  }).map((_, i) => <div key={i} className={`flex-1 h-0.5 rounded-full ${i < 3 ? 'bg-white' : 'bg-white/25'}`} data-id="element-4281" />)}
                  </div>
                </div>
              </div>

              {/* Audio player */}
              <div className="bg-brand-navy text-white rounded-xl p-3 flex items-center gap-2.5" data-id="element-4282">
                <div className="w-8 h-8 rounded-full bg-brand-coral flex items-center justify-center shrink-0" data-id="element-4283">
                  <PlayIcon className="w-3.5 h-3.5 ml-0.5 fill-current" data-id="element-4284" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-4285">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-teal-300" data-id="element-4286">
                    Audio · Guided
                  </div>
                  <div className="text-[11px] font-bold truncate" data-id="element-4287">
                    5-minute morning reset
                  </div>
                  <div className="w-full h-0.5 bg-white/20 rounded-full mt-1.5" data-id="element-4288">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '32%'
                  }} transition={{
                    duration: 1.4,
                    delay: 1.1
                  }} className="h-full bg-brand-teal rounded-full" data-id="element-4289" />
                  </div>
                </div>
                <HeadphonesIcon className="w-3.5 h-3.5 text-teal-300 shrink-0" data-id="element-4290" />
              </div>

              {/* Prompt */}
              <div className="bg-brand-coral/10 border border-brand-coral/20 rounded-xl p-3" data-id="element-4291">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-4292">
                  <span className="text-brand-coral" data-id="element-4293">💭</span>
                  <span className="text-[10px] font-bold text-brand-navy" data-id="element-4294">
                    Today's reflection
                  </span>
                </div>
                <div className="text-[10px] text-slate-700 italic leading-snug" data-id="element-4295">
                  "When did you last feel deeply trusted at work?"
                </div>
              </div>

              {/* Pod assignment */}
              <div className="bg-brand-navy text-white rounded-xl p-3" data-id="element-4296">
                <div className="flex items-center gap-1.5 mb-2" data-id="element-4297">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" data-id="element-4298" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-teal-300" data-id="element-4299">
                    You're in Pod 3
                  </span>
                </div>
                <div className="text-[11px] font-bold mb-2" data-id="element-4300">
                  Meet at Palm Courtyard · 4pm
                </div>
                <div className="flex -space-x-1.5" data-id="element-4301">
                  {[31, 32, 33, 34, 35].map(i => <div key={i} className="w-6 h-6 rounded-full border-2 border-brand-navy overflow-hidden bg-slate-200" data-id="element-4302">
                      <img src={`https://i.pravatar.cc/100?img=${i}`} alt="" className="w-full h-full object-cover" data-id="element-4303" />
                    </div>)}
                  <div className="w-6 h-6 rounded-full border-2 border-brand-navy bg-white text-brand-navy text-[8px] font-bold flex items-center justify-center" data-id="element-4304">
                    +1
                  </div>
                </div>
              </div>

              {/* Activity poll */}
              <div className="bg-white border border-slate-200 rounded-xl p-3" data-id="element-4305">
                <div className="text-[10px] font-bold text-brand-navy mb-2" data-id="element-4306">
                  Pick your morning activity
                </div>
                <div className="space-y-1.5" data-id="element-4307">
                  {[['Sunrise yoga', true], ['Beach hike', false], ['Quiet journaling', false]].map(([l, sel]) => <div key={l as string} className="flex items-center gap-2 text-[10px]" data-id="element-4308">
                      <span className={`w-3 h-3 rounded-full border ${sel ? 'border-brand-teal bg-brand-teal' : 'border-slate-300'} flex items-center justify-center`} data-id="element-4309">
                        {sel ? <span className="w-1 h-1 rounded-full bg-white" data-id="element-4310" /> : null}
                      </span>
                      <span className={sel ? 'text-brand-navy font-bold' : 'text-slate-600'} data-id="element-4311">
                        {l as string}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Video tile */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-4312">
                <div className="relative aspect-video bg-slate-900 overflow-hidden" data-id="element-4313">
                  <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=600&auto=format&fit=crop" alt="" className="w-full h-full object-cover opacity-70" data-id="element-4314" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-4315" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-4316">
                    <span className="w-7 h-7 rounded-full bg-white/95 text-brand-navy flex items-center justify-center shadow" data-id="element-4317">
                      <PlayIcon className="w-3 h-3 ml-0.5 fill-current" data-id="element-4318" />
                    </span>
                  </div>
                  <div className="absolute top-1.5 left-1.5 bg-black/40 backdrop-blur text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" data-id="element-4319">
                    Video · 4:12
                  </div>
                  <div className="absolute bottom-1.5 left-2 right-2 text-white" data-id="element-4320">
                    <div className="text-[8px] font-bold text-teal-300 uppercase tracking-wider" data-id="element-4321">
                      Pre-event briefing
                    </div>
                    <div className="text-[10px] font-bold leading-tight" data-id="element-4322">
                      A note from your facilitator
                    </div>
                  </div>
                </div>
              </div>

              {/* Tomorrow preview */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-id="element-4323">
                <div className="flex items-center justify-between mb-2" data-id="element-4324">
                  <span className="text-[10px] font-bold text-brand-navy" data-id="element-4325">
                    Tomorrow · Day 2
                  </span>
                  <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4326">
                    Preview
                  </span>
                </div>
                <div className="space-y-1 text-[10px] text-slate-600" data-id="element-4327">
                  <div className="flex gap-2" data-id="element-4328">
                    <span className="font-mono text-slate-400 w-10" data-id="element-4329">7:30</span>
                    <span data-id="element-4330">Wellness · sunrise yoga</span>
                  </div>
                  <div className="flex gap-2" data-id="element-4331">
                    <span className="font-mono text-slate-400 w-10" data-id="element-4332">9:00</span>
                    <span data-id="element-4333">Leadership Reset module</span>
                  </div>
                  <div className="flex gap-2" data-id="element-4334">
                    <span className="font-mono text-slate-400 w-10" data-id="element-4335">19:00</span>
                    <span data-id="element-4336">Dine-around in pods</span>
                  </div>
                </div>
              </div>

              {/* Commitment input */}
              <div className="bg-white border border-slate-200 rounded-xl p-3" data-id="element-4337">
                <div className="text-[10px] font-bold text-brand-navy mb-1.5" data-id="element-4338">
                  Your commitment
                </div>
                <div className="text-[10px] text-slate-500 mb-2" data-id="element-4339">
                  One thing you'll take home from today
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] text-slate-400 italic" data-id="element-4340">
                  Type your commitment…
                </div>
              </div>

              {/* Bottom spacer */}
              <div className="h-2" data-id="element-4341" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}